import { Event } from '../types/event.types';
import { FormData } from '../components/organizer/EventForm/types';
import { formatEventTime } from './timezoneUtils';

/**
 * Convertit une heure de fin au format local ISO-like en prenant en compte les événements qui passent minuit
 * @param startDateTime - Date/heure de début de l'événement
 * @param endTimeStr - Heure de fin au format HH:mm
 * @returns string - Heure de fin au format "YYYY-MM-DDTHH:mm" (sans conversion UTC)
 */
export const convertEndTimeToISO = (startDateTime: Date, endTimeStr: string): string => {
  if (!endTimeStr || !startDateTime) {
    return '';
  }

  // Si endTimeStr est déjà au format ISO, le retourner tel quel
  if (endTimeStr.includes('T')) {
    return endTimeStr;
  }

  const [hours, minutes] = endTimeStr.split(':').map(Number);
  const endDateTime = new Date(startDateTime);
  endDateTime.setHours(hours, minutes, 0, 0);
  
  // Si l'heure de fin est avant l'heure de début, c'est probablement le lendemain
  if (endDateTime <= startDateTime) {
    endDateTime.setDate(endDateTime.getDate() + 1);
  }
  
  // Formatage manuel pour éviter la conversion UTC (comme dans calculateEndTimeString)
  const year = endDateTime.getFullYear();
  const month = String(endDateTime.getMonth() + 1).padStart(2, '0');
  const day = String(endDateTime.getDate()).padStart(2, '0');
  const hour = String(endDateTime.getHours()).padStart(2, '0');
  const minute = String(endDateTime.getMinutes()).padStart(2, '0');

  // Retourne "YYYY-MM-DDTHH:mm" en heure locale (sans Z)
  return `${year}-${month}-${day}T${hour}:${minute}`;
};

/**
 * Extrait l'heure d'un endTime pour l'affichage dans un input time
 * Gère plusieurs formats :
 * - "HH:mm" → retourne tel quel
 * - "YYYY-MM-DDTHH:mm" (local, sans Z) → extrait HH:mm
 * - "YYYY-MM-DD HH:mm:ss+00" (TIMESTAMPTZ) → convertit vers timezone locale de l'event
 * - "YYYY-MM-DDTHH:mm:ss.sssZ" (UTC ISO) → convertit vers timezone locale de l'event
 * @param endTimeStr - Heure de fin dans un des formats supportés
 * @param timezone - Timezone IANA de l'event (optionnel, requis pour les formats UTC)
 * @returns string - Heure au format "HH:mm" pour l'input time
 */
export const extractTimeFromEndTime = (endTimeStr: string, timezone?: string): string => {
  if (!endTimeStr) {
    return '';
  }

  // Si c'est déjà au format HH:mm (5 chars, avec :), le retourner tel quel
  if (endTimeStr.length === 5 && endTimeStr.includes(':')) {
    return endTimeStr;
  }

  // If the string contains a timezone indicator (+00, Z, +05:30, etc.)
  // → it's a UTC/TIMESTAMPTZ value, must convert to event's timezone
  const hasTimezoneInfo = endTimeStr.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(endTimeStr) || endTimeStr.includes('+00');
  if (hasTimezoneInfo && timezone) {
    const converted = formatEventTime(endTimeStr, timezone);
    if (converted) return converted;
  }

  // Format TIMESTAMPTZ without timezone param: "2026-01-22 22:00:00+00" (espace séparateur)
  // Fallback: extract raw time (may be incorrect without timezone conversion)
  if (endTimeStr.includes(' ')) {
    const timePart = endTimeStr.split(' ')[1]; // "22:00:00+00"
    if (timePart) {
      return timePart.substring(0, 5);
    }
  }

  // Format ISO local (no Z): "YYYY-MM-DDTHH:mm" (T séparateur)
  if (endTimeStr.includes('T')) {
    const timePart = endTimeStr.split('T')[1];
    if (timePart) {
      return timePart.substring(0, 5);
    }
  }

  return endTimeStr;
};

/**
 * Assure qu'un endTime existe, en le calculant automatiquement si nécessaire (datetime + 1h)
 * @param startDateTime - Date/heure de début de l'événement
 * @param endTimeStr - Heure de fin au format HH:mm ou ISO (optionnel)
 * @returns string - Heure de fin au format "YYYY-MM-DDTHH:mm"
 */
export const ensureEndTime = (startDateTime: Date, endTimeStr?: string): string => {
  // Si endTime est déjà fourni et non vide, le convertir au format ISO si nécessaire
  if (endTimeStr && endTimeStr.trim() !== '') {
    return convertEndTimeToISO(startDateTime, endTimeStr);
  }

  // Sinon, calculer datetime + 1h
  const endDate = new Date(startDateTime);
  endDate.setHours(endDate.getHours() + 1);

  // Formatage manuel pour éviter la conversion UTC
  const year = endDate.getFullYear();
  const month = String(endDate.getMonth() + 1).padStart(2, '0');
  const day = String(endDate.getDate()).padStart(2, '0');
  const hour = String(endDate.getHours()).padStart(2, '0');
  const minute = String(endDate.getMinutes()).padStart(2, '0');

  const calculatedEndTime = `${year}-${month}-${day}T${hour}:${minute}`;
  console.log(`✨ [ensureEndTime] endTime auto-calculé: ${calculatedEndTime} (datetime + 1h)`);
  return calculatedEndTime;
};



// Nettoie les données du formulaire pour un événement unique
export const cleanEventData = (formData: FormData): Partial<Event> => {
  const cleanedData: Partial<Event> = {
    organizerId: formData.organizerId,
    organizerContact: formData.organizerContact,
    title: formData.title.trim(),
    description: formData.description?.trim(),
    location: formData.location?.trim(),
    locationName: formData.locationName?.trim(),
    latitude: formData.latitude,
    longitude: formData.longitude,
    timezone: formData.timezone, // IANA timezone pour l'affichage correct des heures
    tags: formData.tags,
    image: formData.image,
    endTime: formData.endTime?.trim(),
    ticketingUrl: formData.ticketingUrl?.trim(),
    prices: (formData.prices || [])
      .map(price => ({
        amount: Number(price.amount),
        description: price.description.trim(),
        currency: price.currency
      }))
      .filter(price => !isNaN(price.amount) && price.amount >= 0 && price.currency),
    status: formData.status
  };

  // Liste des champs obligatoires qui ne doivent pas être supprimés
  // 🔧 PROTECTION CRITIQUE : Ajouter 'id' pour éviter la corruption !
  const requiredFields = ['id', 'organizerId', 'title', 'status'] as const;

  // Supprimer les champs vides ou undefined de manière typée
  (Object.keys(cleanedData) as Array<keyof Event>).forEach(key => {
    if (!requiredFields.includes(key as typeof requiredFields[number]) && 
        (cleanedData[key] === undefined || cleanedData[key] === '')) {
      delete cleanedData[key];
    }
  });

  // Si prices est un tableau vide, le supprimer
  if (cleanedData.prices?.length === 0) {
    delete cleanedData.prices;
  }

  return cleanedData;
};

// Vérifie si un événement est passé
export const isEventPast = (datetime: Date): boolean => {
  const now = new Date();
  return datetime < now;
};

/**
 * Trie les événements par date
 */
export const sortEventsByDate = (events: Event[]): Event[] => {
  return [...events].sort((a, b) => {
    if (!a.datetime || !b.datetime) return 0;
    return a.datetime.getTime() - b.datetime.getTime();
  });
};

/**
 * Filtre les événements à venir
 */
export const filterUpcomingEvents = (events: Event[]): Event[] => {
  const now = new Date();
  return events.filter(event => {
    if (!event.datetime) return false;
    return event.datetime > now;
  });
};
